import { FC, useState, useMemo, useEffect } from 'react';
import { AnalyticSearchRequestDto } from 'src/generated/services/TFinancialApi';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import { SelectChangeEvent, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MultipleSelectDropdown } from '../filter/MultipleSelectDropdown';
import { granularityMapper } from '../analyticsMapper';

type TabType = 'TRENDS' | 'TRADE_LANE' | 'SHIPMENT_DETAILS';

interface ChartFilterTabsProps {
  selectedGranularity: AnalyticSearchRequestDto['granularity'];
  onGranularityChange: (granularity: AnalyticSearchRequestDto['granularity']) => void;
}

const GRANULARITY_OPTIONS: Record<TabType, Array<AnalyticSearchRequestDto['granularity']>> = {
  TRENDS: ['YEARLY', 'QUARTERLY', 'MONTHLY', 'WEEKLY', 'DAILY'],
  TRADE_LANE: ['COUNTRY', 'CITY_HUB', 'COMPANY'],
  SHIPMENT_DETAILS: ['SERVICE_PROVIDER_CODE', 'MODALITY', 'VEHICLE_ID'],
};

const TAB_LABELS: Record<TabType, string> = {
  TRENDS: 'Trends',
  TRADE_LANE: 'Trade Lane',
  SHIPMENT_DETAILS: 'Shipment Details',
};

const StyledBox = styled(Box)(({ theme }) => ({
  '& .MuiFormControl-root': {
    width: '280px',
    minWidth: '250px',
    [theme.breakpoints.down(1520)]: {
      width: '250px',
      minWidth: '250px',
    },
  },
  '& .MuiInputBase-root': {
    borderRadius: '8px',
    padding: '4px',
    lineHeight: '15px',
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#404040',
  },
  '& .MuiSelect-select': {
    minHeight: 'auto !important',
  },
  '& .MuiPaper-root': {
    boxShadow: '0px 12px 12px 0px rgba(34, 65, 102, 0.09)',
    border: '1px solid #DFDFDF',
  },
}));

export const ChartFilterTabs: FC<ChartFilterTabsProps> = ({ selectedGranularity, onGranularityChange }) => {
  // Determine active tab based on selected granularity
  const getActiveTab = (granularity: AnalyticSearchRequestDto['granularity']): TabType => {
    if (GRANULARITY_OPTIONS.TRENDS.includes(granularity)) return 'TRENDS';
    if (GRANULARITY_OPTIONS.TRADE_LANE.includes(granularity)) return 'TRADE_LANE';
    if (GRANULARITY_OPTIONS.SHIPMENT_DETAILS.includes(granularity)) return 'SHIPMENT_DETAILS';
    return 'TRENDS';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getActiveTab(selectedGranularity));

  // Remember user's selection for each tab
  const [tabSelections, setTabSelections] = useState<Record<TabType, AnalyticSearchRequestDto['granularity']>>({
    TRENDS: selectedGranularity && GRANULARITY_OPTIONS.TRENDS.includes(selectedGranularity) ? selectedGranularity : 'MONTHLY',
    TRADE_LANE: selectedGranularity && GRANULARITY_OPTIONS.TRADE_LANE.includes(selectedGranularity) ? selectedGranularity : 'COUNTRY',
    SHIPMENT_DETAILS: selectedGranularity && GRANULARITY_OPTIONS.SHIPMENT_DETAILS.includes(selectedGranularity) ? selectedGranularity : 'SERVICE_PROVIDER_CODE',
  });

  // Setup form control for dropdown
  const { control, setValue } = useForm({
    defaultValues: {
      granularity: selectedGranularity,
    },
  });

  // Update form value when selectedGranularity changes
  useEffect(() => {
    setValue('granularity', selectedGranularity);
  }, [selectedGranularity, setValue]);

  // Update tabSelections when selectedGranularity changes from external source (e.g., loading saved report)
  useEffect(() => {
    const currentTab = getActiveTab(selectedGranularity);
    setActiveTab(currentTab);
    setTabSelections((prev) => ({
      ...prev,
      [currentTab]: selectedGranularity,
    }));
  }, [selectedGranularity]);

  // Convert current options to dropdown format
  const currentOptions = useMemo(
    () =>
      GRANULARITY_OPTIONS[activeTab].map((option) => ({
        label: granularityMapper(option),
        value: option,
      })),
    [activeTab]
  );

  const handleTabClick = (tab: TabType) => {
    setActiveTab(tab);
    // Use the remembered selection for this tab
    const rememberedSelection = tabSelections[tab];
    onGranularityChange(rememberedSelection);
  };

  const handleGranularityChange = (event: SelectChangeEvent<string | string[]>) => {
    const value = event.target.value as AnalyticSearchRequestDto['granularity'];

    // Save the selection for the current tab
    setTabSelections((prev) => ({
      ...prev,
      [activeTab]: value,
    }));

    onGranularityChange(value);
  };

  return (
    <div className='flex items-center justify-between gap-16'>
      {/* Tab Buttons - Left Side */}
      <div className='flex items-center gap-8 bg-[#F3F3F3] rounded-lg border border-neutral-20 p-4 w-[28rem] xl:w-[31.25rem]'>
        {(Object.keys(TAB_LABELS) as TabType[]).map((tab) => (
          <button
            key={tab}
            type='button'
            onClick={() => handleTabClick(tab)}
            className={clsx(
              'flex-1 py-8 rounded-md text-12 xl:text-14 font-medium transition-all text-center',
              activeTab === tab ? 'bg-white text-neutral-90 shadow-sm' : 'bg-transparent text-neutral-60 hover:bg-neutral-20'
            )}
          >
            {TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* Select Dropdown - Right Side */}
      <StyledBox>
        <MultipleSelectDropdown
          name='granularity'
          label='Select View'
          control={control}
          defaultValue={selectedGranularity}
          options={currentOptions}
          multiple={false}
          onChangeCustom={handleGranularityChange}
        />
      </StyledBox>
    </div>
  );
};
